import React from 'react';

import { arrayOfLength } from '@pkg/utils';

import {
    type DataTableCellProps,
    type DataTableCommonProps,
    type DataTableHeadingProps,
    type DataTableProps,
    type DataTableSkeletonCellProps,
    type DataTableSkeletonRowsProps,
} from './DataTable.types';
import { Collection, DataValue, LoadAction, SkeletonCollection, SkeletonDataValue, SortAction } from './components';
import { useFadableColumns } from './useFadableColumns';

import {
    StyledDataTable,
    Table,
    TableBody,
    TableBodyCell,
    TableBodyHeader,
    TableHead,
    TableHeadHeader,
    TableHeadHeaderInterior,
    TableRow,
    Wrapper,
} from './DataTable.style';

export function DataTable({ children, lastCellAlignEnd = false, separated = false }: DataTableProps) {
    // NOTE: This hook is hopefully temporary.
    const { cssCustomProps, handleSetRef } = useFadableColumns(separated);

    return (
        <StyledDataTable
            ref={handleSetRef}
            style={cssCustomProps}
            data-table-align={lastCellAlignEnd ? 'end' : 'start'}
            data-table-collapse={separated ? 'separate' : 'collapse'}
        >
            <Table>{children}</Table>
        </StyledDataTable>
    );
}

function Row({ children }: DataTableCommonProps) {
    return <TableRow>{children}</TableRow>;
}

function Head({ children }: DataTableCommonProps) {
    return (
        <TableHead>
            <Row>{children}</Row>
        </TableHead>
    );
}

function ColumnHeader<T>({ label, columnId, sortDirection, disabled = false, onSort }: DataTableHeadingProps<T>) {
    const contentMarkup = onSort ? (
        <SortAction
            label={label}
            columnId={columnId}
            sortDirection={sortDirection}
            disabled={disabled}
            onSort={onSort}
        />
    ) : (
        <SortAction.Label>{label}</SortAction.Label>
    );

    return (
        <TableHeadHeader $sortOffset={Boolean(onSort)}>
            <TableHeadHeaderInterior>{contentMarkup}</TableHeadHeaderInterior>
        </TableHeadHeader>
    );
}

function Body({ children }: DataTableCommonProps) {
    return <TableBody>{children}</TableBody>;
}

function RowHeader({ children }: DataTableCommonProps) {
    return <TableBodyHeader>{children}</TableBodyHeader>;
}

function Cell({ children, emptyState = false }: DataTableCellProps) {
    const fullWidth = emptyState ? 99 : undefined;
    return <TableBodyCell colSpan={fullWidth}>{children}</TableBodyCell>;
}

function EmptyState({ children }: DataTableCommonProps) {
    return (
        <Row>
            <Cell emptyState>{children}</Cell>
        </Row>
    );
}

function SkeletonRow({ children }: DataTableCommonProps) {
    return <Row>{children}</Row>;
}

function SkeletonCell({ children, header = false }: DataTableSkeletonCellProps) {
    return header ? <RowHeader>{children}</RowHeader> : <Cell>{children}</Cell>;
}

///
/// Stand alone + optional components

export function DataTableWrapper({ children }: DataTableCommonProps) {
    return <Wrapper>{children}</Wrapper>;
}

export function DataTableSkeletonRows({ columns = 2, rows = 4 }: DataTableSkeletonRowsProps) {
    // Subtract 1 to ignore the leading "sticky" column.
    const supportingColumns = Math.max(1, columns - 1);

    const cellsMarkup = arrayOfLength(supportingColumns).map((index) => (
        <DataTable.SkeletonRow.SkeletonCell key={`SkeletonRow-${index}`}>
            <DataTable.SkeletonDataValue />
        </DataTable.SkeletonRow.SkeletonCell>
    ));

    const rowsMarkup = arrayOfLength(rows).map((index) => (
        <DataTable.SkeletonRow key={`SkeletonRow-${index}`}>
            <DataTable.SkeletonRow.SkeletonCell header>
                <DataTable.SkeletonCollection />
            </DataTable.SkeletonRow.SkeletonCell>

            {cellsMarkup}
        </DataTable.SkeletonRow>
    ));

    return <>{rowsMarkup}</>;
}

///
/// Stand alone + optional

DataTableWrapper.LoadAction = LoadAction;

///
/// Table elements

Head.Header = ColumnHeader;
Row.Header = RowHeader;
Row.Cell = Cell;
Body.Row = Row;

DataTable.Head = Head;
DataTable.Body = Body;
DataTable.EmptyState = EmptyState;

///
/// Content elements

DataTable.Collection = Collection;
DataTable.DataValue = DataValue;

///
/// Skeleton elements

SkeletonRow.SkeletonCell = SkeletonCell;

DataTable.SkeletonRow = SkeletonRow;
DataTable.SkeletonCollection = SkeletonCollection;
DataTable.SkeletonDataValue = SkeletonDataValue;
